/**
 * Tipo de colecta. Son TRES, no dos:
 *  - RECEPCIÓN        el comercio trajo los paquetes a la bodega y un supervisor los
 *                     recibió. No hay ruta.
 *  - RETIRO           fuimos a buscarlos y se llevaron a un hub (cross-docking).
 *  - RETIRO PICK & GO fuimos a buscarlos y salieron directo a reparto, sin pasar por
 *                     bodega. Se le paga distinto al repartidor.
 *
 * "Retiro" a secas se mantiene como etiqueta del caso hub a propósito: es lo que ya
 * decía todo el histórico, y renombrarlo habría cambiado el valor de cada registro
 * anterior sin que nada haya cambiado en la operación.
 *
 * ── Cómo se resuelve ────────────────────────────────────────────────────────────
 * Primero el ORIGEN, mirando dos señales en orden:
 *  1. `source`, que el backend estampa desde que existe la Recepción en bodega.
 *  2. La ausencia de ruta, para los registros anteriores a ese campo. Una recepción
 *     nunca tiene ruta y un retiro siempre la tiene, así que clasifica bien el
 *     histórico sin backfill.
 *
 * Después, y sólo si es un retiro, la MODALIDAD: `deliveryMode`, que el backend congela
 * al cerrar la colecta. Ausente ⇒ 'hub'. NO se deduce de la ruta acá: el front no la
 * tiene, y un Pick & Go inventado sería indistinguible de uno real.
 *
 * Es el MISMO criterio que usan microservice-pickups y el informe de colectas
 * (`collect-type.ts`), a propósito: si cada pantalla dedujera distinto, el mismo
 * registro aparecería con dos tipos según dónde se lo mire y no habría forma de saber
 * cuál miente.
 */
export const COLLECT_TYPE = {
  PICKUP: 'route_pickup',
  PICKUP_PICK_AND_GO: 'route_pickup_pick_and_go',
  RECEPTION: 'warehouse_reception',
};

export const COLLECT_TYPE_LABEL = {
  [COLLECT_TYPE.PICKUP]: 'Retiro',
  [COLLECT_TYPE.PICKUP_PICK_AND_GO]: 'Retiro Pick & Go',
  [COLLECT_TYPE.RECEPTION]: 'Recepción',
};

/** Clase modificadora del badge, para no repetir el mapeo en cada vista. */
export const COLLECT_TYPE_MODIFIER = {
  [COLLECT_TYPE.PICKUP]: 'pickup',
  [COLLECT_TYPE.PICKUP_PICK_AND_GO]: 'pickandgo',
  [COLLECT_TYPE.RECEPTION]: 'reception',
};

/** Devuelve siempre uno de los tres tipos: la columna nunca queda vacía. */
export function resolveCollectType(pickup) {
  const source = pickup?.source;

  if (source === COLLECT_TYPE.RECEPTION) return COLLECT_TYPE.RECEPTION;

  if (source !== COLLECT_TYPE.PICKUP) {
    // Sin `source`: se deduce por la ruta. `routeId` puede venir como ObjectId poblado.
    const routeId = pickup?.routeId ?? pickup?.route?._id ?? pickup?.route;
    const hasRoute = typeof routeId === 'string' ? routeId.trim().length > 0 : Boolean(routeId);
    if (!hasRoute) return COLLECT_TYPE.RECEPTION;
  }

  return pickup?.deliveryMode === 'pick_and_go'
    ? COLLECT_TYPE.PICKUP_PICK_AND_GO
    : COLLECT_TYPE.PICKUP;
}

export function collectTypeLabel(pickup) {
  return COLLECT_TYPE_LABEL[resolveCollectType(pickup)];
}

export function collectTypeModifier(pickup) {
  return COLLECT_TYPE_MODIFIER[resolveCollectType(pickup)];
}
